import React from "react";
import { CheckCircle, ShowerHead, Clock, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import EnteteLanding from "../components/EnteteLanding";
import PiedLanding from "../components/PiedLanding";
import SimulateurRapide from "../components/SimulateurRapide";
import BoutonRappel from "../components/BoutonRappel";
import { serviceJsonLd } from "../utils/donneesStructurees";
import { formaterEuros, AIDE_MAX, PLAFOND_TRAVAUX } from "../utils/plafonds";

/**
 * Page d'atterrissage Google Ads — adaptation de salle de bain.
 *
 * Volontairement en noindex et absente du sitemap : elle traite le même sujet
 * que /salle-de-bain, qui est référencée et travaillée pour l'organique. Deux
 * pages proches en concurrence sur les mêmes requêtes se cannibalisent, et le
 * site a déjà des URL en « Détectée, actuellement non indexée » dans Search
 * Console. Google Ads n'exige pas qu'une page de destination soit indexée.
 * Pour l'ouvrir à l'organique : passer noindex à false ci-dessous, ajouter
 * l'URL au sitemap et lui donner des liens entrants depuis le site.
 */

// Cas de financement bâtis sur les règles publiées : plafond de travaux de
// 22 000 € HT, 70 % pour un ménage très modeste, 50 % pour un ménage modeste.
// Les montants de travaux sont des ordres de grandeur de chantier, pas des
// devis constatés — d'où le mot « exemple » et l'avertissement en bas.
const EXEMPLES = [
  {
    titre: "Remplacement d'une baignoire par une douche de plain-pied",
    travaux: 6500,
    taux: 0.7,
    profil: "Ménage très modeste, 1 personne",
    detail: "Dépose de la baignoire, receveur extra-plat, paroi, robinetterie, sol antidérapant.",
  },
  {
    titre: "Salle de bain complète adaptée",
    travaux: 12000,
    taux: 0.7,
    profil: "Ménage très modeste, 2 personnes",
    detail: "Douche sécurisée, lavabo accessible en fauteuil, WC surélevé, barres d'appui, éclairage.",
  },
  {
    titre: "Douche sécurisée et barres d'appui",
    travaux: 5000,
    taux: 0.5,
    profil: "Ménage modeste, 2 personnes",
    detail: "Remplacement du bac, barres d'appui, siège de douche, revêtement antidérapant.",
  },
];

const EQUIPEMENTS = [
  { image: "/images/douche-securisee.webp", nom: "Douche de plain-pied", texte: "Receveur extra-plat, sans marche à franchir, paroi coulissante." },
  { image: "/images/sol-antiderapant.webp", nom: "Sol antidérapant", texte: "Revêtement classé antidérapant, posé sur toute la pièce." },
  { image: "/images/main-courante.webp", nom: "Barres d'appui", texte: "Points d'appui fixés dans le mur porteur, aux endroits utiles." },
  { image: "/images/lavabo-adapte.webp", nom: "Lavabo accessible", texte: "Vasque dégagée en dessous, utilisable assis ou en fauteuil." },
  { image: "/images/wc-pmr.webp", nom: "WC surélevé", texte: "Hauteur d'assise relevée, barre de relevage latérale." },
  { image: "/images/eclairage.webp", nom: "Éclairage renforcé", texte: "Interrupteurs accessibles, éclairage sans zone d'ombre." },
];

const LandingSalleDeBain = () => (
  <div className="min-h-screen bg-white">
    <Seo
      titre="Adapter votre salle de bain avec MaPrimeAdapt' | Île-de-France"
      description="Douche de plain-pied, barres d'appui, sol antidérapant : jusqu'à 15 400 € d'aide MaPrimeAdapt' en Île-de-France. Vérifiez votre éligibilité en 30 secondes, accompagnement AMO gratuit."
      chemin="/adaptation-salle-de-bain-idf"
      image="/images/douche-securisee.webp"
      noindex
      donnees={[
        serviceJsonLd({
          nom: "Adaptation de salle de bain MaPrimeAdapt'",
          description:
            "Montage du dossier MaPrimeAdapt' et accompagnement pour l'adaptation d'une salle de bain : douche de plain-pied, sol antidérapant, barres d'appui, WC surélevé.",
          url: "/adaptation-salle-de-bain-idf",
        }),
      ]}
    />
    <EnteteLanding />

    <main id="contenu">
      {/* Accroche + simulateur immédiatement visible */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-10 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-orange-700 text-white rounded-full text-sm font-medium">
                <ShieldCheck className="h-4 w-4 mr-2" aria-hidden="true" />
                AMO agréé ANAH — Île-de-France
              </div>

              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                {/* L'espace explicite est nécessaire : le span est en block,
                    donc invisible à l'écran, mais sans lui le texte lu par
                    Google et les lecteurs d'écran se colle au précédent. */}
                Douche sécurisée, barres d&apos;appui, sol antidérapant —{" "}
                <span className="text-orange-700 block mt-2">
                  jusqu&apos;à {formaterEuros(AIDE_MAX.tresModestes)} d&apos;aides
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-gray-900 leading-relaxed">
                MaPrimeAdapt&apos; finance jusqu&apos;à <strong>70 % de vos travaux</strong>{" "}
                d&apos;adaptation. Nous montons le dossier auprès de l&apos;ANAH à votre
                place, de la visite à domicile au versement de l&apos;aide.
                <strong> Notre accompagnement ne vous coûte rien</strong> : il est pris
                en charge par le dispositif.
              </p>

              <ul className="space-y-3">
                {[
                  "Diagnostic à domicile gratuit, sans engagement",
                  "Dossier ANAH monté et suivi par nos soins",
                  "Artisans du réseau, devis conformes au dossier",
                  "Vous ne payez rien avant l'accord écrit de l'ANAH",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-gray-900 text-lg">{point}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <BoutonRappel
                  libelle="Être rappelé gratuitement"
                  variant="default"
                  className="bg-orange-700 hover:bg-orange-800 text-white text-lg min-h-[56px] w-full sm:w-auto"
                />
                <a
                  href="tel:0939035444"
                  className="inline-flex items-center justify-center gap-2 border-2 border-orange-700 text-orange-800 hover:bg-orange-50 rounded-lg px-6 text-lg font-semibold min-h-[56px] w-full sm:w-auto"
                >
                  09 39 03 54 44
                </a>
              </div>
            </div>

            <SimulateurRapide />
          </div>
        </div>
      </section>

      {/* Équipements financés */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Ce que MaPrimeAdapt&apos; finance dans une salle de bain
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Les travaux éligibles ne se limitent pas à la douche : tout ce qui
                sécurise l&apos;usage de la pièce entre dans le dossier.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {EQUIPEMENTS.map((eq) => (
                <div key={eq.nom} className="rounded-xl overflow-hidden border-2 border-gray-100 bg-white">
                  <img
                    src={eq.image}
                    alt={eq.nom}
                    width="600"
                    height="400"
                    loading="lazy"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <ShowerHead className="h-5 w-5 text-orange-700" aria-hidden="true" />
                      {eq.nom}
                    </h3>
                    <p className="text-gray-700">{eq.texte}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <BoutonRappel
                libelle="Faire le point sur mon projet"
                variant="default"
                className="bg-orange-700 hover:bg-orange-800 text-white text-lg min-h-[56px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Exemples de financement */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Trois exemples de financement
              </h2>
              <p className="text-xl text-gray-700">
                Calculés sur les règles du dispositif : 70 % pour un ménage très
                modeste, 50 % pour un ménage modeste, dans la limite de{" "}
                {formaterEuros(PLAFOND_TRAVAUX)} de travaux HT.
              </p>
            </div>

            <div className="space-y-6">
              {EXEMPLES.map((ex) => {
                const aide = Math.round(ex.travaux * ex.taux);
                return (
                  <div key={ex.titre} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                    <div className="grid md:grid-cols-2 gap-6 items-center">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{ex.titre}</h3>
                        <p className="text-gray-700 mb-3">{ex.detail}</p>
                        <p className="text-sm font-medium text-orange-800">{ex.profil}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-5 space-y-2">
                        <div className="flex justify-between text-gray-900">
                          <span>Montant des travaux</span>
                          <span className="font-medium">{formaterEuros(ex.travaux)}</span>
                        </div>
                        <div className="flex justify-between text-green-800 font-bold">
                          <span>Aide MaPrimeAdapt&apos; ({Math.round(ex.taux * 100)} %)</span>
                          <span>− {formaterEuros(aide)}</span>
                        </div>
                        <div className="flex justify-between text-gray-900 pt-2 border-t border-orange-200 font-bold text-lg">
                          <span>Reste à charge</span>
                          <span>{formaterEuros(ex.travaux - aide)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-sm text-gray-700 mt-8 text-center max-w-3xl mx-auto">
              Ces exemples illustrent le calcul de l&apos;aide sur des montants de travaux
              courants. Ils ne valent pas devis : le montant retenu dépend des travaux
              validés après la visite à domicile. D&apos;autres aides peuvent s&apos;y
              ajouter — caisse de retraite, aide départementale, TVA à 5,5 %.
            </p>
          </div>
        </div>
      </section>

      {/* Rappel du parcours + dernier appel à l'action */}
      <section className="py-16 lg:py-24 bg-orange-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Un seul interlocuteur, du premier appel au versement
            </h2>

            <div className="grid sm:grid-cols-3 gap-6 my-10 text-left">
              {[
                ["1. Nous vous rappelons", "Sous 24 h ouvrées, pour vérifier l'éligibilité et fixer la visite."],
                ["2. Visite et dossier", "Diagnostic autonomie à domicile, devis, dépôt du dossier à l'ANAH."],
                ["3. Travaux et versement", "Vous démarrez après l'accord écrit. L'aide est versée sur factures."],
              ].map(([titre, texte]) => (
                <div key={titre} className="bg-orange-800 rounded-xl p-5">
                  <h3 className="font-bold text-lg mb-2">{titre}</h3>
                  <p className="text-orange-50">{texte}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <BoutonRappel
                libelle="Choisir un créneau de rappel"
                variant="default"
                className="bg-white text-orange-800 hover:bg-orange-50 text-lg min-h-[56px] w-full sm:w-auto"
              />
              <a
                href="tel:0939035444"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-orange-800 rounded-lg px-6 text-lg font-semibold min-h-[56px] w-full sm:w-auto"
              >
                09 39 03 54 44
              </a>
            </div>

            <p className="text-orange-100 mt-6 flex items-center justify-center gap-2">
              <Clock className="h-5 w-5" aria-hidden="true" />
              Du lundi au jeudi 10 h - 17 h, vendredi 10 h - 13 h
            </p>
          </div>
        </div>
      </section>
    </main>

    <PiedLanding />
  </div>
);

export default LandingSalleDeBain;
